import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ColorTheme = 'light' | 'dark';
export type UiLocale = 'zh-CN' | 'en';
export type Translate = (chinese: string, english: string) => string;

interface UiPreferences {
  theme: ColorTheme;
  locale: UiLocale;
  t: Translate;
  toggleTheme(): void;
  toggleLocale(): void;
}

const THEME_KEY = 'tcode.theme';
const LOCALE_KEY = 'tcode.locale';

const UiPreferencesContext = createContext<UiPreferences>({
  theme: 'light',
  locale: 'zh-CN',
  t: (chinese) => chinese,
  toggleTheme: () => undefined,
  toggleLocale: () => undefined,
});

export function UiPreferencesProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ColorTheme>(initialTheme);
  const [locale, setLocale] = useState<UiLocale>(initialLocale);
  const t = useCallback<Translate>(
    (chinese, english) => (locale === 'zh-CN' ? chinese : english),
    [locale],
  );

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useLayoutEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem(LOCALE_KEY, locale);
  }, [locale]);

  const value = useMemo<UiPreferences>(
    () => ({
      theme,
      locale,
      t,
      toggleTheme: () => setTheme((current) => (current === 'light' ? 'dark' : 'light')),
      toggleLocale: () => setLocale((current) => (current === 'zh-CN' ? 'en' : 'zh-CN')),
    }),
    [locale, t, theme],
  );

  return <UiPreferencesContext.Provider value={value}>{children}</UiPreferencesContext.Provider>;
}

export function useUiPreferences(): UiPreferences {
  return useContext(UiPreferencesContext);
}

function initialTheme(): ColorTheme {
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function initialLocale(): UiLocale {
  const stored = window.localStorage.getItem(LOCALE_KEY);
  if (stored === 'zh-CN' || stored === 'en') return stored;
  return window.navigator.language.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en';
}
